import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, Megaphone, AlertTriangle, Sparkles, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const READ_KEY = "bbf_ann_read";

function loadRead() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveRead(set) {
  localStorage.setItem(READ_KEY, JSON.stringify([...set]));
}

const ICONS = { info: Megaphone, alert: AlertTriangle, new_product: Sparkles };
const COLORS = {
  info: "#2B5A73",
  alert: "#D94532",
  new_product: "#3D6B42",
};

export default function AnnouncementBell({ brand }) {
  const [items, setItems] = useState([]);
  const [read, setRead] = useState(loadRead());
  const [open, setOpen] = useState(false);
  const dark = brand?.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";

  useEffect(() => {
    if (!brand) return;
    api.get("/announcements", { params: { brand: brand.key } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [brand]);

  const unread = items.filter((a) => !read.has(a.id)).length;
  const markAllRead = () => {
    const ns = new Set([...read, ...items.map((a) => a.id)]);
    setRead(ns); saveRead(ns);
  };

  if (!brand || items.length === 0) return null;

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) markAllRead(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="announcements-bell" className="relative p-2 rounded-md transition-colors" style={{ color: fg }}>
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="ann-unread-count" className="absolute -top-1 -right-1 bg-[#D94532] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[480px] overflow-auto">
        <div className="px-3 py-2 flex items-center justify-between">
          <p className="font-heading font-semibold text-[#2D2622]">Announcements</p>
          <span className="text-xs text-[#8C8581]">{items.length} total</span>
        </div>
        <DropdownMenuSeparator />
        {items.map((a) => {
          const Icon = ICONS[a.type] || Megaphone;
          return (
            <div key={a.id} data-testid={`announcement-${a.id}`} className="px-3 py-2.5 border-b border-[#2D2622]/5 last:border-0">
              <div className="flex items-start gap-2.5">
                <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: COLORS[a.type] || "#5C5753" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-[#2D2622]">{a.title}</p>
                    {a.pinned && <span className="text-[9px] uppercase tracking-wider text-[#C85A32] font-bold">Pinned</span>}
                  </div>
                  <p className="text-xs text-[#5C5753] mt-1 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                  <p className="text-[10px] text-[#8C8581] mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Sticky banner shown at top of Shop for pinned/high-priority announcements */
export function AnnouncementBanner({ brand }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(new Set(loadRead()));

  useEffect(() => {
    if (!brand) return;
    api.get("/announcements", { params: { brand: brand.key } })
      .then(({ data }) => setItems(data.filter((a) => a.pinned)))
      .catch(() => setItems([]));
  }, [brand]);

  const visible = items.filter((a) => !dismissed.has(a.id));
  if (!visible.length) return null;
  const a = visible[0];
  const Icon = ICONS[a.type] || Megaphone;

  return (
    <div data-testid="announcement-banner" className="mb-6 rounded-xl p-4 flex items-start gap-3"
      style={{ background: `${COLORS[a.type]}11`, border: `1px solid ${COLORS[a.type]}55` }}>
      <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: COLORS[a.type] }} />
      <div className="flex-1">
        <p className="font-heading font-semibold text-[#2D2622]">{a.title}</p>
        <p className="text-sm text-[#5C5753] mt-0.5 leading-relaxed whitespace-pre-wrap">{a.body}</p>
      </div>
      <button data-testid={`dismiss-banner-${a.id}`} onClick={() => {
        const ns = new Set([...dismissed, a.id]);
        setDismissed(ns); saveRead(ns);
      }} className="p-1 hover:bg-black/5 rounded">
        <X className="w-4 h-4 text-[#8C8581]" />
      </button>
    </div>
  );
}
